import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";

export const NUTRITION_CHANGED = "vyv:nutrition-changed";
export const notifyNutritionChanged = () => window.dispatchEvent(new Event(NUTRITION_CHANGED));
export type NutritionStatus = "loading" | "ready" | "error" | "signed-out";

/** Account-bound reads shared by the active Nutrition routes. */
export function useNutritionResource<T>(loader: (userId: string, assertCurrent: () => void) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [status, setStatus] = useState<NutritionStatus>("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const owner = useRef<string | null | undefined>(undefined);
  const generation = useRef(0);
  const request = useRef(0);
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    const ticket = ++request.current;
    let epoch = generation.current;
    const current = () => mounted.current && ticket === request.current && epoch === generation.current;
    const assertCurrent = () => { if (!current()) throw new Error("Nutrition session changed"); };
    setIsRefreshing(true);
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      assertCurrent();
      if (error) throw error;
      const next = user?.id ?? null;
      if (owner.current !== next) {
        epoch = ++generation.current; owner.current = next;
        setUserId(next); setData(null); setCheckedAt(null);
      }
      if (!next) { setStatus("signed-out"); return; }
      const result = await loader(next, assertCurrent);
      assertCurrent(); setData(result); setStatus("ready"); setCheckedAt(Date.now());
    } catch (error) {
      if (current()) { console.error("[Nutrition] read failed", error); setStatus("error"); }
    } finally {
      if (current()) setIsRefreshing(false);
    }
  }, [loader]);

  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (!mounted.current || owner.current === next) return;
      owner.current = next; generation.current++; request.current++;
      setUserId(next); setData(null); setCheckedAt(null); setIsRefreshing(false);
      setStatus(next ? "loading" : "signed-out");
      if (next) queueMicrotask(() => { if (mounted.current) void refresh(); });
    });
    void refresh();
    const changed = () => { if (mounted.current && owner.current) void refresh(); };
    window.addEventListener(NUTRITION_CHANGED, changed);
    return () => { mounted.current = false; generation.current++; request.current++; subscription.unsubscribe(); window.removeEventListener(NUTRITION_CHANGED, changed); };
  }, [refresh]);

  useAgendaAutoRefresh({ checkedAt, nextEvent: null, loading: isRefreshing, error: status === "error", enabled: !!userId || status === "error", refresh });
  const capture = useCallback(() => {
    const epoch = generation.current;
    return () => mounted.current && owner.current === userId && !!userId && generation.current === epoch;
  }, [userId]);
  return { data, userId, status, isRefreshing, checkedAt, refresh, capture };
}
